// Thin client over the shared aircargomcp API (Hono/Bun). Both surfaces hit the same backend.
import type { AirRateQuote, AwbTrackingData } from '@aircargomcp/types'

export const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:8787'

// The API returns { data } on success, { error } on failure (shape from packages/types AppError).
export interface ApiError {
  code:
    | 'not_found'
    | 'unsupported_carrier'
    | 'invalid_reference'
    | 'upstream_error'
    | 'blocked'
    | 'timeout'
    | 'rate_limited'
    | 'gated' // free web-tier limit reached (add an API key for unlimited)
  message: string
  retryable?: boolean
}

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: ApiError }

async function unwrap<T>(res: Response): Promise<ApiResult<T>> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    return { ok: false, error: { code: 'upstream_error', message: 'Bad response from server' } }
  }
  const b = body as { data?: T; error?: ApiError }
  if (res.ok && b.data !== undefined) return { ok: true, data: b.data }
  if (b.error) return { ok: false, error: b.error }
  return { ok: false, error: { code: 'upstream_error', message: `HTTP ${res.status}` } }
}

// There is deliberately NO client for the keyed GET /track/:awb here. This bundle never sends an
// Authorization header, so that call can only answer 401 — which is exactly what the homepage tracker
// did to every real AWB while such a client existed (fixed 2026-09-07). Keyed access is for API customers
// and the MCP server, not for the anonymous web.

/**
 * GET /track/public/:awb — the free, no-API-key WEB tier the public tracker uses. Resolves real AWBs
 * anonymously (cost-bounded server-side); a 429 comes back as { ok:false, error:{ code:'gated' } } once the
 * free web limit is reached, which the UI turns into an "add an API key" upsell.
 */
export async function trackAwbPublic(awb: string): Promise<ApiResult<AwbTrackingData>> {
  try {
    const res = await fetch(`${API_BASE}/track/public/${encodeURIComponent(awb.trim())}`)
    return unwrap<AwbTrackingData>(res)
  } catch {
    return { ok: false, error: { code: 'timeout', message: 'Network error. Is the API running?' } }
  }
}

export interface PrefixInfo {
  prefix: string
  iata: string
  name: string
  site: string
  platform?: string
}

/** GET /prefix/:code — resolve a 3-digit AWB prefix to its issuing airline. */
export async function lookupPrefix(code: string): Promise<ApiResult<PrefixInfo>> {
  try {
    const res = await fetch(`${API_BASE}/prefix/${encodeURIComponent(code.trim())}`)
    return unwrap<PrefixInfo>(res)
  } catch {
    return { ok: false, error: { code: 'timeout', message: 'Network error. Is the API running?' } }
  }
}

export interface RateQuery {
  origin: string
  destination: string
  weight_kg: number
  volume_cm3?: number
}

/** POST /rates/quote — price a lane against the best available rate card. */
export async function quoteRate(q: RateQuery): Promise<ApiResult<AirRateQuote>> {
  try {
    const res = await fetch(`${API_BASE}/rates/quote`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(q),
    })
    return unwrap<AirRateQuote>(res)
  } catch {
    return { ok: false, error: { code: 'timeout', message: 'Network error. Is the API running?' } }
  }
}
